import { randomBytes } from "pvcryptojs";
import { FSWrappedKey, FSMessage, FSType, FSRequest, FSBlockType } from "src/common/keys/protos/collections_pb";
import {
  Account, Helpers, UUID, AppUserKey, DriveErrorMessages, PermissionSetType, getDirSymmKey, getNewWrappedDirKey, FSTypeMapType, BulkUpdateParams,
  FSLinkMap, FSDirMap, FSFileMap
} from "src/common";
import _ from "lodash";

// NOTE: Number of nodes processed before yielding to the browser event loop during the share-promote crypto walk.
const SNAPSHOT_YIELD_BATCH_SIZE = 50;

// Description: Yield control back to the event loop so the renderer can repaint and process input during heavy seal/unseal loops.
function yieldToEventLoop(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0));
}

export interface IndexedSnapshot {
  dirs: FSDirMap;
  files: FSFileMap;
  links: FSLinkMap;
}

export class Snapshot {
  readonly dirsMap: FSDirMap;
  readonly filesMap: FSFileMap;
  public snapNodeMap: Map<string, SnapshotNode>;
  public linksList: FSLinkMap;
  // NOTE: Node counter shared across the recursive buildSnapNodes walk so yields batch by total nodes, not per directory level.
  // Assumes one walk per instance (a fresh Snapshot is created per share); it is never reset, so reusing an instance
  // for a second walk would carry the counter over.
  private nodes_since_yield: number = 0;
  constructor(
    public snapshot: IndexedSnapshot,
    public default_collection_id: UUID,
    public share_name: Uint8Array
  ) {
    this.snapshot = snapshot;
    this.dirsMap = snapshot.dirs;
    this.filesMap = snapshot.files;
    this.linksList = snapshot.links;
    this.snapNodeMap = new Map();
    this.default_collection_id = default_collection_id;
    this.share_name = share_name;
  }

  public containsLink(): Boolean {
    return this.linksList.size > 0;
  }

  // NOTE: onProgress fires on each yield so the caller can nudge the progress bar during this walk.
  // We don't know the node count up front, so it's just forward movement, not a real percentage.
  public async buildSnapNodes(reader_key: AppUserKey, old_reader_key: AppUserKey, dir: FSMessage.Dir, parent_id: string, parent_version: string, onProgress?: () => void) {
    for (const entry of dir.getEntriesList()) {
      if (this.snapNodeMap.has(entry.getId_asB64())) {
        continue;
      }
      switch (entry.getType()) {
        case FSType.DIR:
          const entry_id = entry.getId_asB64();
          const directory = this.dirsMap.get(entry_id);
          if (!!directory) {
            const new_version = Helpers.b64Encode(randomBytes(32));
            const wrapped_dir_key = directory.getWrappedDirKey();
            this.snapNodeMap.set(
              entry_id,
              new SnapshotNode(
                entry_id,
                new_version,
                entry.getName_asU8(),
                parent_id,
                parent_version,
                true,
                false,
                wrapped_dir_key,
                undefined,
                dir.getLastLocalFsUpdate()
              )
            );
            await this.buildSnapNodes(reader_key, old_reader_key, directory, entry_id, new_version, onProgress);
          }
          break;
        case FSType.FILE:
          const file_entry_id = entry.getId_asB64();
          const file = this.filesMap.get(file_entry_id);
          if (!!file) {
            const blocks = await this.getDataListBlocks(file, old_reader_key, reader_key);
            this.snapNodeMap.set(
              file_entry_id,
              new SnapshotNode(
                file_entry_id,
                Helpers.b64Encode(randomBytes(32)),
                entry.getName_asU8(),
                parent_id,
                parent_version,
                false,
                false,
                undefined,
                blocks,
                file.getLastLocalFsUpdate(),
              ),
            );
          }
          break;
      }
      // NOTE: Counter is shared across recursion, so we yield every SNAPSHOT_YIELD_BATCH_SIZE nodes overall regardless of tree shape.
      this.nodes_since_yield++;
      if (this.nodes_since_yield >= SNAPSHOT_YIELD_BATCH_SIZE) {
        this.nodes_since_yield = 0;
        await yieldToEventLoop();
        onProgress?.();
      }
    }
  }

  // Description: Get Files Blocks
  private async getDataListBlocks(file: FSMessage.File, old_reader_key: AppUserKey, reader_key: AppUserKey) {
    const blocks = await Promise.all(_.map(file.getDataList(), async (data_block: FSMessage.Block) => {
      const block = new FSRequest.BulkUpdate.Block();
      const wrapped_key = data_block.getWrappedKey();
      const unwrapped_key = !!wrapped_key ? await old_reader_key.encryption_key.unseal(wrapped_key.getWrappedKey_asU8()) : null;
      const rewrapped_key = !!unwrapped_key ? await reader_key.encryption_key.seal(unwrapped_key) : null;
      if (!!rewrapped_key) {
        const fswrappedkey = new FSWrappedKey();
        fswrappedkey.setWrappedKey(rewrapped_key);
        fswrappedkey.setKeyVersion(reader_key.key_version);
        block.setWrappedKey(fswrappedkey);
      } else {
        console.error(DriveErrorMessages.error_wrapping_block_key);
      }
      block.setBlockSize(data_block.getBlockSize() || 0);
      block.setHash(data_block.getHash());
      const id = `${Helpers.hexEncode(this.default_collection_id.Bytes())}/${data_block.getId()}`;
      block.setBlockType(FSBlockType.POINTER);
      const encrypted_id = await reader_key.public_user_key.public_key.seal(Helpers.utf8Encode(id));
      block.setId(Helpers.hexEncode(encrypted_id));
      return block;
    }));
    return blocks;
  }

  // Description: Build the FSRequest.Create[], FSRequest.BulkUpdate.Block[] and FSRequest.Update[] fro BulkUpdate
  // NOTE: onProgress fires after each batch so the caller can keep the bar moving through the rest of the rewrap.
  public async buildUpdateMessage(current_account: Account, default_permissions: PermissionSetType, new_acl_reader: AppUserKey, onProgress?: () => void):
    Promise<BulkUpdateParams> {
    const blocks: FSRequest.BulkUpdate.Block[] = [];
    const create_dir: FSRequest.Create[] = [];
    const create_file: FSRequest.Create[] = [];
    const updates: FSRequest.Update[] = [];
    // NOTE: Process node ids in batches with a yield between slices so the renderer stays responsive on large snapshots.
    const node_ids = Array.from(this.snapNodeMap.keys());
    for (let i = 0; i < node_ids.length; i += SNAPSHOT_YIELD_BATCH_SIZE) {
      const slice = node_ids.slice(i, i + SNAPSHOT_YIELD_BATCH_SIZE);
      await Promise.all(_.map(slice, async (id: string) => {
        const value = this.snapNodeMap.get(id);
        if (!!value) {
          if (value.is_directory) {
            if (!value.is_root) { // NOTE: Do not include the root directory in BULK UPDATE
              const symm_key = !!value.wrappedDirKey ? await getDirSymmKey(value.wrappedDirKey, current_account, default_permissions) : null;
              const new_wrapped_key = !!symm_key ? await getNewWrappedDirKey(symm_key, current_account, new_acl_reader) : null;
              !!new_wrapped_key ? create_dir.push(this._buildNewCreate(id, value, FSType.DIR, new_wrapped_key)) : console.error(DriveErrorMessages.error_getting_wrapped_key);
            }
          } else {
            if (!!value.blocks) {
              // NOTE: Handle FSRequest.Create, FSRequest.BulkUpdate.Block[], FSRequest.Update
              create_file.push(this._buildNewCreate(id, value, FSType.FILE));
              // Add To FSRequest.BulkUpdate.Block[]
              const block_ids = _.compact(_.map(value.blocks, (block: FSRequest.BulkUpdate.Block) => block.getId()));
              for (const block of value.blocks) {
                blocks.push(block);
              }
              !!block_ids && block_ids.length > 0 ?
                updates.push(this._buildUpdate(id, value, block_ids)) :
                console.error(DriveErrorMessages.error_getting_file_blocks);
            } else {
              console.error(DriveErrorMessages.error_getting_file_blocks);
            }
          }
        }

        return value;
      }));
      await yieldToEventLoop();
      onProgress?.();
    }

    // NOTE: Create Directories first to avoid Parent not found error
    return {
      create: create_dir.concat(create_file),
      updates,
      blocks
    };
  }

  // Description: Build the FSRequest.Create object
  private _buildNewCreate(id: string, value: SnapshotNode, type: FSTypeMapType, fs_wrapped_key?: FSWrappedKey): FSRequest.Create {
    const create = new FSRequest.Create();
    create.setParentId(value.parent_id);
    create.setParentVer(value.parent_version);
    create.setId(id);
    create.setVersion(value.version);
    create.setName(value.name);
    create.setType(type);
    !!value.last_local_fs_update && create.setLastLocalFsUpdate(value.last_local_fs_update);
    !!fs_wrapped_key && create.setWrappedDirKey(fs_wrapped_key); // NOT FOR FILES
    return create;
  }

  // Description: Build the FSRequest.Update object
  private _buildUpdate(id: string, value: SnapshotNode, block_ids: string[]): FSRequest.Update {
    const change = new FSRequest.Update.Change();
    change.setOp(FSRequest.Update.Op.ALTER);
    change.setDataList(block_ids);
    const update = new FSRequest.Update();
    update.setId(id);
    update.setOldVersion(value.version);
    update.setNewVersion(randomBytes(32));
    update.setChangesList([change]);
    !!value.last_local_fs_update && update.setLastLocalFsUpdate(value.last_local_fs_update);
    return update;
  }

  // Description: In a multi-page snapshot, directory children might be split across pages, so we are combining the entries for the directories
  // with the same id so that all entries are together.
  static getIndexedSnapshot(snapshot: FSMessage.Snapshot): IndexedSnapshot {
    const dirs = new Map<string, FSMessage.Dir>();
    snapshot.getDirsList().forEach((value) => {
      const id = value.getId_asB64();
      const existing_dir = dirs.get(id);
      if (dirs.has(value.getId_asB64()) && !!existing_dir) {
        const new_entries = _.uniqBy(existing_dir.getEntriesList().concat(value.getEntriesList()), (entry => entry.getId_asB64()));
        existing_dir.setEntriesList(new_entries);
      } else {
        dirs.set(value.getId_asB64(), value);
      }
    });
    const files = new Map();
    snapshot.getFilesList().forEach((value) => {
      files.set(value.getId_asB64(), value);
    });
    const links = new Map();
    snapshot.getLinksList().forEach((value) => {
      links.set(value.getId_asB64(), value);
    });
    return { dirs, files, links };
  }

  // Description: Build a new FSMessage.Snapshot for fetching
  static buildSnapshot(seq: number, subseq: number, response_snapshot: FSMessage.Snapshot, previous_snapshot: FSMessage.Snapshot): FSMessage.Snapshot {
    const new_snapshot = new FSMessage.Snapshot();
    const directories = previous_snapshot.getDirsList().concat(response_snapshot.getDirsList());
    const files = previous_snapshot.getFilesList().concat(response_snapshot.getFilesList());
    const links = previous_snapshot.getLinksList().concat(response_snapshot.getLinksList());
    new_snapshot.setDirsList(directories);
    new_snapshot.setFilesList(files);
    new_snapshot.setLinksList(links);
    new_snapshot.setSeq(response_snapshot.getSeq() || seq);
    new_snapshot.setCursorSeq(response_snapshot.getCursorSeq() || seq);
    new_snapshot.setCursorSubseq(response_snapshot.getCursorSubseq() || subseq);
    new_snapshot.setHasMore(!!previous_snapshot.getHasMore());
    return new_snapshot;
  }
}

export class SnapshotNode {
  constructor(
    public id: string | Uint8Array,
    public version: string | Uint8Array,
    public name: Uint8Array,
    public parent_id: string | Uint8Array,
    public parent_version: string | Uint8Array,
    public is_directory: boolean,
    public is_root: boolean = false,
    public wrappedDirKey?: FSWrappedKey,
    public blocks?: FSRequest.BulkUpdate.Block[],
    public last_local_fs_update?: string
  ) { }


}
